import React, { memo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { MousePointer, Hand, Share2, SquareEqual, Lightbulb, Grid2X2, Edit3, Square, Circle, ArrowUp, Type, Image } from 'lucide-react';

interface ToolStatusIndicatorProps {
  selectedTool?: string;
  isCircuitMode?: boolean;
  isControlPlacementMode?: boolean;
  selectedControlType?: string | null;
}

export const ToolStatusIndicator = memo(function ToolStatusIndicator({ 
  selectedTool,
  isCircuitMode,
  isControlPlacementMode,
  selectedControlType 
}: ToolStatusIndicatorProps) {
  const activeTool = useAppStore((state) => state.ui.toolbar.activeTool);
  const activeMode = useAppStore((state) => state.ui.toolbar.activeMode);
  const activeAnnotationType = useAppStore((state) => state.annotations.activeAnnotationType);

  // Determine the label and icon to show
  let label = '';
  let Icon = MousePointer;
  
  if (isCircuitMode || activeMode === 'circuit') {
    label = 'Load Creation Tool';
    Icon = Share2;
  } else if (isControlPlacementMode && selectedControlType) {
    // Format control type name properly
    const controlTypeLabel = selectedControlType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    if (selectedControlType === 'centralized-keypad') {
      label = 'Insert LV Keypad';
    } else {
      label = `Insert ${controlTypeLabel}`;
    }
    Icon = SquareEqual;
  } else if (selectedTool === 'create-fixture') {
    label = 'Create a Fixture';
    Icon = Lightbulb;
  } else if (selectedTool === 'create-room') {
    label = 'Create Room';
    Icon = Grid2X2;
  } else if (activeTool === 'pan') {
    label = 'Pan Mode';
    Icon = Hand;
  } else if (activeMode === 'annotation' && activeAnnotationType) {
    // Handle annotation tools
    switch (activeAnnotationType) {
      case 'line':
        label = 'Draw Line';
        Icon = Edit3;
        break;
      case 'rectangle':
        label = 'Draw Rectangle';
        Icon = Square;
        break;
      case 'circle':
        label = 'Draw Circle';
        Icon = Circle;
        break;
      case 'arrow':
        label = 'Draw Arrow';
        Icon = ArrowUp;
        break;
      case 'text':
        label = 'Add Text';
        Icon = Type;
        break;
      case 'image':
        label = 'Add Image';
        Icon = Image;
        break;
      default:
        label = 'Annotation Mode';
        Icon = Edit3;
    }
  } else {
    label = 'Select Mode';
    Icon = MousePointer;
  }

  return (
    <div className="absolute bottom-[env(safe-area-inset-bottom,16px)] left-4 bg-white rounded-lg shadow-md px-3 py-2 flex items-center gap-2 z-50"
         style={{ marginBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium">
        {label}
      </span>
    </div>
  );
});

ToolStatusIndicator.displayName = 'ToolStatusIndicator';